import dayjs from "dayjs";
import type {
    Options,
    XAxisOptions,
    TooltipOptions,
    SeriesLineOptions,
    TooltipFormatterCallbackFunction,
    AxisLabelsFormatterCallbackFunction,
} from "highcharts";

import { LineGraph, shortMonthAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes, fontVariants } from "@similarweb/styles";

import { formatNumber } from "../utils/numberFormatters";

type CustomOptions = Omit<Options, "xAxis" | "tooltip"> & {
    xAxis: XAxisOptions & {
        formatter?: AxisLabelsFormatterCallbackFunction;
        categories: string[];
    };
    tooltip: TooltipOptions & {
        formatter: TooltipFormatterCallbackFunction;
    };
};

/**
 * A simple utility function to create a line chart config with defaults used in Demand Analysis
 * @param {CustomOptions} options - Highcharts.Options with required xAxis.categories and tooltip.formatter options
 * @returns {Options} The line chart config
 */
export const chartOptions = ({ xAxis, tooltip, ...options }: CustomOptions): Options => {
    const { categories, ...xAxisOptions } = xAxis;
    const { formatter: tooltipFormatter, ...tooltipOptions } = tooltip;

    return {
        // ...options,
        chart: {
            height: 350,
            ...options?.chart,
        },
        plotOptions: {
            series: {
                pointPlacement: categories.length > 1 ? "on" : undefined,
            },
            ...options?.plotOptions,
        },
        xAxis: {
            type: "category",
            categories,
            startOnTick: true,
            endOnTick: true,
            tickmarkPlacement: "on",
            tickInterval: 1,
            tickPixelInterval: 10,
            tickWidth: 1,
            tickColor: colorsPalettes.carbon[100],
            labels: {
                formatter(ctx) {
                    if (xAxisOptions?.labels?.formatter) {
                        return xAxisOptions.labels.formatter.call(this, ctx);
                    }
                    return dayjs.utc(this.value as string).format(shortMonthAndYear);
                },
                style: {
                    color: colorsPalettes.carbon[300],
                    fontSize: `${fontVariants.dataTable2.size}px`,
                    fontWeight: `${fontVariants.dataTable2.weight}`,
                    fontFamily: fontVariants.dataTable2.family,
                },
            },
            ...xAxisOptions,
        },
        yAxis: {
            labels: {
                formatter() {
                    return formatNumber(this.value as number, true);
                },
                style: {
                    color: colorsPalettes.carbon[400],
                    fontSize: `${fontVariants.dataTable2.size}px`,
                    fontWeight: `${fontVariants.dataTable2.weight}`,
                    fontFamily: fontVariants.dataTable2.family,
                },
            },
            ...options?.yAxis,
        },
        tooltip: {
            outside: false,
            useHTML: true,
            padding: 0,
            formatter: tooltipFormatter,
            ...tooltipOptions,
        },
        ...options,
    } satisfies Options;
};

interface IDemandAnalysisLineChartProps {
    options: Options;
    series: SeriesLineOptions[];
}

export const DemandAnalysisLineChart = ({ options, series }: IDemandAnalysisLineChartProps) => {
    return (
        <LineGraph
            shouldPresentLegend={false}
            graphConfig={options}
            data={series}
        />
    );
};
